/**
 * TripDetailPageNew — full feature parity with old TripDetailPage.
 * Includes: back link, inline Add Visit form, owner-only share link, owner-only delete, swipeable cards, PullToRefresh.
 */
import React from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTrip, useDeleteTrip } from '../hooks/useTrips';
import { useTripsVisits, useCreateVisit, useDeleteVisit } from '../hooks/useVisits';
import { useParks } from '../hooks/useParks';
import { useShareLink, useGenerateShareLink } from '../hooks/useShareLink';
// DataCard, DataCardRow, PriorityBadge imports — reserved for future use
import { VisitCard } from '../design-system/primitives/VisitCard';
import { EmptyState } from '../components/shared/EmptyState';
import { SwipeableCard } from '../components/shared/SwipeableCard';
import { PullToRefresh } from '../components/shared/PullToRefresh';
// SearchInput import — reserved for future use
import { CustomSelect } from '../CustomSelect';
import { Ticket, Share2, Plus, ArrowLeft } from 'lucide-react';

function formatDate(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}


function getStatus(visitDate: string, isActive?: boolean): { label: string; color: string } {
  if (isActive) return { label: 'Active', color: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' };
  const todayStr = new Date().toISOString().split('T')[0];
  if (visitDate === todayStr) return { label: 'Today!', color: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300' };
  const v = new Date(visitDate + 'T00:00:00');
  const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1); tomorrow.setHours(0, 0, 0, 0);
  if (v >= tomorrow) return { label: 'Upcoming', color: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' };
  return { label: 'Completed', color: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300' };
}

export function TripDetailPageNew(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const fromHome = (location.state as any)?.from === 'home';
  const queryClient = useQueryClient();
  const [newVisitPark, setNewVisitPark] = React.useState('');
  const [newVisitDate, setNewVisitDate] = React.useState('');
  const [showAddVisitForm, setShowAddVisitForm] = React.useState(false);
  const [copyFeedback, setCopyFeedback] = React.useState(false);

  const { data: parks } = useParks();
  const { data: trip, isLoading } = useTrip(id!);
  const { data: tripVisits = [] } = useTripsVisits(id ?? '');
  const isOwner = trip?.isOwner ?? false;
  const { data: shareLink } = useShareLink(isOwner ? id! : '');
  const deleteTrip = useDeleteTrip();
  const createVisit = useCreateVisit();
  const deleteVisit = useDeleteVisit();
  const generateShareLink = useGenerateShareLink();

  const sortedVisits = tripVisits
    .sort((a: any, b: any) => new Date(a.visitDate).getTime() - new Date(b.visitDate).getTime());

  const handleAddVisit = () => {
    if (!newVisitPark || !newVisitDate) return;
    createVisit.mutate({ parkName: newVisitPark, visitDate: newVisitDate, tripId: trip!.id });
    setNewVisitPark('');
    setNewVisitDate('');
    setShowAddVisitForm(false);
  };

  const handleShare = async () => {
    if (!isOwner || !id) return;
    let shareUrl = shareLink?.shareUrl;
    if (!shareUrl) {
      try { await generateShareLink.mutateAsync(id); } catch { /* ignore */ }
      shareUrl = shareLink?.shareUrl;
    }
    if (!shareUrl) return;
    const url = shareUrl.startsWith('http') ? shareUrl : `${window.location.origin}${shareUrl}`;
    try { await navigator.clipboard.writeText(url); } catch {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    setCopyFeedback(true);
    setTimeout(() => setCopyFeedback(false), 2000);
  };

  if (isLoading) return <div className="space-y-3">{[1, 2].map((i) => <div key={i} className="skeleton-row animate-pulse h-20 bg-gray-200 dark:bg-gray-700 rounded" />)}</div>;
  if (!trip) return <div className="text-center py-12 text-gray-600 dark:text-gray-400">Trip not found</div>;

  return (
    <PullToRefresh onRefresh={async () => {
      await queryClient.refetchQueries({ queryKey: ['trips'] });
      await queryClient.refetchQueries({ queryKey: ['trips-visits', id] });
    }}>
      <div className="space-y-6">
        {/* Back link */}
        <button onClick={() => fromHome ? navigate('/') : navigate('/my-trips')}
          className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
          <ArrowLeft className="w-5 h-5" />
          <span>{fromHome ? 'Back to home' : 'Back to trips'}</span>
        </button>

        {/* Header */}
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{trip.name}</h1>
            {isOwner && (
              <button onClick={handleShare}
                className="ml-2 p-1.5 rounded-lg text-gray-500 hover:text-disney-blue-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-disney-blue-400 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                aria-label="Copy share link" title="Copy share link"
                disabled={generateShareLink.isPending}>
                {copyFeedback ? <span className="text-xs text-disney-green-600 dark:text-disney-green-400">Copied!</span> : <Share2 className="w-5 h-5" />}
              </button>
            )}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {formatDate(trip.startDate)} — {formatDate(trip.endDate)}
          </p>
        </div>

        {/* Visits */}
        {sortedVisits.length === 0 ? (
          <EmptyState icon={Ticket} iconColor="text-gray-400" title="No visits yet"
            subtitle="Add a park visit to start planning." />
        ) : (
          <ul className="space-y-3">
            {sortedVisits.map((visit: any) => {
              const status = getStatus(visit.visitDate, visit.isActive);
              return (
                <li key={visit.id}>
                  <SwipeableCard onSwipeLeft={() => deleteVisit.mutate(visit.id)}
                    onShare={trip.isOwner ? handleShare : undefined}>
                    <VisitCard
                      parkName={visit.parkName}
                      visitDate={visit.visitDate}
                      visitPeriod={visit.visitPeriod}
                      attractionCount={visit.selectedAttractionCount ?? 0}
                      status={status}
                      noRing
                      onClick={() => navigate(`/visit/${visit.id}`)}
                    />
                  </SwipeableCard>
                </li>
              );
            })}
          </ul>
        )}

        {/* Add visit form */}
        {showAddVisitForm ? (
          <div className="bg-white dark:bg-surface-dark-card rounded-xl p-4 shadow-card dark:ring-1 dark:ring-white/[0.06] space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">Add Visit</h3>
              <button onClick={() => setShowAddVisitForm(false)} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <CustomSelect id="new-visit-park" label="" value={newVisitPark}
              options={(parks ?? []).map((park) => ({ value: park.name, label: park.name }))}
              onChange={setNewVisitPark} placeholder="Select a park" />
            <input type="date" value={newVisitDate} onChange={(e) => setNewVisitDate(e.target.value)}
              className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-surface-dark-card px-3 py-2 text-sm" />
            <button onClick={handleAddVisit} disabled={!newVisitPark || !newVisitDate || createVisit.isPending}
              className="w-full px-4 py-2 bg-disney-blue-600 text-white rounded-lg text-sm font-medium hover:bg-disney-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {createVisit.isPending ? 'Adding...' : 'Add Visit'}
            </button>
          </div>
        ) : (
          <button onClick={() => setShowAddVisitForm(true)} disabled={createVisit.isPending}
            className="w-full px-4 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl text-sm text-gray-500 dark:text-gray-400 hover:border-disney-blue-400 hover:text-disney-blue-600 dark:hover:border-disney-blue-500 dark:hover:text-disney-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2">
            <Plus className="w-4 h-4" /> Add visit
          </button>
        )}

        {/* Delete trip (owner only) */}
        {isOwner && (
          <div className="text-center">
            <button onClick={() => { if (window.confirm('Are you sure you want to delete this trip?')) { deleteTrip.mutate(trip.id); navigate('/my-trips'); } }}
              className="text-sm text-red-600 dark:text-red-400 hover:underline">
              Delete trip
            </button>
          </div>
        )}
      </div>
    </PullToRefresh>
  );
}
